import { FC } from 'react';
import styled, { css } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { checkIfWindows, replaceQuotes } from '@/utils/helpers';

interface BaseImageData {
  image: File;
  text?: string;
  croppedImage?: string;
  quotes?: boolean;
  brakes?: boolean;
  textPosition?: string;
  [key: string]: any;
}

const Dialog = styled(StyledDialog)`
  padding: 0;
  text-align: left;
  width: 648px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: 4px;
  padding: 24px;
  position: relative;
  box-shadow: 0px 2px 8px rgba(34, 34, 34, 0.06);
`;

const ModalContent = styled.div`
  padding: 24px;
`;

const ImageHolder = styled.div`
  position: relative;
  overflow: hidden;
`;

const Image = styled.img`
  width: 600px;
  height: 630px;
  object-fit: cover;
`;

const BrakesP = styled.p`
  white-space: nowrap;
`;

const CustomText = styled.div<{ $top?: boolean; $isWindows: boolean }>`
  position: absolute;
  width: 100%;
  padding: 0 10px;
  bottom: 35px;
  font-size: 45px;
  line-height: 1.2;
  text-align: center;
  color: #000;
  font-family: CustomArial;
  font-weight: 400;
  -webkit-text-shadow: 1.5px 0 0 #000;
  -moz-text-shadow: 1.5px 0 0 #000;
  -ms-text-shadow: 1.5px 0 0 #000;
  -o-text-shadow: 1.5px 0 0 #000;
  text-shadow: 1.5px 0 0 #000;
  letter-spacing: 1.2px;
  p {
    background-color: white;
    display: inline-block;
    margin: 0;
    padding: 14px 40px 16px;
  }
  ${({ $top }) =>
    $top &&
    css`
      bottom: auto;
      top: 35px;
    `}
  ${({ $isWindows }) =>
    $isWindows &&
    css`
      -webkit-text-shadow: 1.9px 0 0 #000;
      -moz-text-shadow: 1.9px 0 0 #000;
      -ms-text-shadow: 1.9px 0 0 #000;
      -o-text-shadow: 1.9px 0 0 #000;
      text-shadow: 1.9px 0 0 #000;
    `}
`;

interface Props {
  onClose: () => void;
  image: BaseImageData;
}

const PreviewModal: FC<Props> = ({ onClose, image }) => {
  const text = image.quotes ? `"${image.text || ''}"` : image.text || '';

  const renderTextWithLineBreaks = (text: string) => {
    const lines = text.split('\n');

    return (
      <>
        {lines.map((line) => (
          <>
            {line}
            <br />
          </>
        ))}
      </>
    );
  };

  return (
    <Modal zIndex={2}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalHeader>
          <Text $type="button">Image Preview</Text>
          <CloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <ImageHolder>
            <Image
              src={
                image.croppedImage
                  ? image.croppedImage
                  : URL.createObjectURL(image.image)
              }
            />
            {!!image.text && (
              <CustomText
                $isWindows={checkIfWindows()}
                $top={image.textPosition === 'top'}
              >
                {!!image.brakes ? (
                  <BrakesP>
                    {renderTextWithLineBreaks(replaceQuotes(text))}
                  </BrakesP>
                ) : (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: distributeTextCanvas(replaceQuotes(text), {
                        maxWidth: 465,
                        fontFamily: 'CustomArial',
                        minFontSize: 45,
                      }),
                    }}
                  ></p>
                )}
              </CustomText>
            )}
          </ImageHolder>
        </ModalContent>
      </Dialog>
    </Modal>
  );
};

export default PreviewModal;
